import type { ReactNode, PointerEvent as ReactPointerEvent } from "react";
import { createPortal } from "react-dom";
import { useDraggablePosition } from "src/hooks/useDraggablePosition";
import { cn } from "src/utilities/shadcnUtils";

/**
 * Bottom-right resize grip. Drag from the corner to resize. Tagged with
 * `data-no-drag` so the drag handler in `useDraggablePosition` doesn't try
 * to start a move at the same time (resize takes precedence).
 */
function ResizeGrip({ onPointerDown }: { onPointerDown: (e: ReactPointerEvent<HTMLElement>) => void }) {
  return (
    <button
      type="button"
      data-no-drag
      aria-label="Resize window"
      onPointerDown={onPointerDown}
      className="absolute bottom-0.5 right-0.5 w-2 h-2 cursor-nwse-resize z-10 p-0 border-0 bg-transparent"
    >
      <span
        className="block w-full h-full"
        style={{
          // Two diagonal lines forming the classic resize-grip dots, painted
          // via background gradients so we don't need an icon font / svg.
          background:
            "linear-gradient(135deg, transparent 50%, rgba(0,0,0,0.25) 50%, rgba(0,0,0,0.25) 60%, transparent 60%, transparent 75%, rgba(0,0,0,0.25) 75%, rgba(0,0,0,0.25) 85%, transparent 85%)",
        }}
      />
    </button>
  );
}

interface CompactWindowFrameProps {
  /**
   * Stable id used as the localStorage key suffix for position persistence.
   * Pick something unique per surface (e.g. `compact-compose` for the single
   * email compose, `compact-agent:<sessionId>` per agent session so multiple
   * agent windows remember their own positions).
   */
  storageId: string;
  width?: number;
  height?: number;
  /** First-open offset from the top-right corner of the viewport, in px. */
  defaultOffset?: { right: number; top: number };
  /**
   * CSS selector picking the drag handle inside `children`. The frame's
   * onPointerDown only initiates a drag when the pointer-down originates
   * inside an element matching this selector. Defaults to anything tagged
   * `data-drag-handle`.
   */
  handleSelector?: string;
  /** Buttons inside the handle won't start a drag — handled by default. */
  ignoreSelector?: string;
  className?: string;
  children: ReactNode;
}

/**
 * Reusable shrunk-down floating window with drag-to-move + viewport clamping
 * + per-window position persistence. Wrap any modal body in this to give it
 * a "picture-in-picture" presentation that the user can move around.
 */
export function CompactWindowFrame({
  storageId,
  width = 460,
  height = 600,
  defaultOffset = { right: 16, top: 16 },
  handleSelector = "[data-drag-handle]",
  ignoreSelector = "button, input, textarea, [contenteditable], [data-no-drag]",
  className,
  children,
}: CompactWindowFrameProps) {
  const initialLeft =
    typeof window !== "undefined" ? Math.max(0, window.innerWidth - width - defaultOffset.right) : 0;
  const { style, onPointerDown, onResizePointerDown, onResizeBottomLeftPointerDown } = useDraggablePosition({
    storageKey: storageId,
    defaultPos: { left: initialLeft, top: defaultOffset.top },
    width,
    height,
    handleSelector,
    ignoreSelector,
  });

  // Portal into document.body so a CSS `transform` / `contain` / `filter` on
  // any ancestor doesn't hijack our fixed positioning (the most common cause
  // of a "stuck inside its container" bug).
  if (typeof document === "undefined") return null;
  return createPortal(
    <div
      onPointerDown={onPointerDown}
      style={style}
      className={cn(
        "fixed z-50 rounded-lg shadow-2xl dark:shadow-black/60 border border-border dark:border-white/10 bg-card overflow-hidden flex flex-col",
        className
      )}
    >
      {children}
      <ResizeGrip onPointerDown={onResizePointerDown} />
      <BottomLeftResizeGrip onPointerDown={onResizeBottomLeftPointerDown} />
    </div>,
    document.body
  );
}

/**
 * Bottom-left resize grip. Drag from the corner to resize while keeping the
 * window's right edge anchored. Cursor is `nesw-resize` (NE-SW diagonal).
 */
function BottomLeftResizeGrip({
  onPointerDown,
}: {
  onPointerDown: (e: ReactPointerEvent<HTMLElement>) => void;
}) {
  return (
    <button
      type="button"
      data-no-drag
      aria-label="Resize from bottom-left"
      onPointerDown={onPointerDown}
      className="absolute bottom-0.5 left-0.5 w-2 h-2 cursor-nesw-resize z-10 p-0 border-0 bg-transparent"
    >
      <span
        className="block w-full h-full"
        style={{
          // Mirror of ResizeGrip's 135° gradient — 225° points the gradient
          // toward the bottom-left, so the dot weight sits in that corner.
          background:
            "linear-gradient(225deg, transparent 50%, rgba(0,0,0,0.25) 50%, rgba(0,0,0,0.25) 60%, transparent 60%, transparent 75%, rgba(0,0,0,0.25) 75%, rgba(0,0,0,0.25) 85%, transparent 85%)",
        }}
      />
    </button>
  );
}
